'use strict';

define('vb/action/builtin/forkAction',[
  'vb/action/action',
  'vb/private/log',
],
(Action, Log) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  const logger = Log.getLogger('/vb/action/action/forkAction');

  /**
   * The fork action allows multiple action steps to be run in parallel (forking each set of action steps), then
   * having a common outcome that awaits and collects the results (joining each set back together).
   *
   * A fork action has an arbitrary set of outcomes whose action sub-chains will all run in parallel. A special
   * outcome 'join' will be followed once all the sub-chains complete processing.
   *
   * If successful, the result is a mapping from the outcome id's of the sub-chains to their outcome/result payload.
   * If unsuccessful, this will return a 'failure' outcome with the result being the outcome of the failed
   * forked sub-chain.
   *
   * @extends Action
   * @category Action
   */
  class ForkAction extends Action {
    /**
     * Constructs a new instance of the ForkAction class.
     *
     * @param {string} id - The unique identifier for the action.
     * @param {string} label - The human-readable label for the action.
     */
    constructor(id, label) {
      super(id, label);
      this.log = logger;
      this.actionChain = null;
      this.outcomesMap = null; // backward-compatibility, this should be removed
    }

    /**
     * Performs the fork action by running all the specified actions in parallel and collecting their results.
     *
     * @param {Object} [parameters] - Optional parameters object containing the actions to be performed.
     * @param {Object} [parameters.actions] - An object mapping action IDs to their corresponding actions.
     *
     * @returns {Promise<Outcome>} A promise resolving to the outcome of the fork action, which is either:
     *   - A success outcome with the ID "join" and a result object containing the results of all the actions,
     *   - A failure outcome with an error message and the partial results collected so far.
     */
    perform(parameters) {
      return new Promise((resolve) => {
        const results = {};

        // go through all the outcomes and run in parallel
        const ongoingPromises = [];

        const actionMap = (parameters && parameters.actions) || this.outcomesMap;
        // abort if the actions is an array
        if (!actionMap || typeof actionMap !== 'object' || Array.isArray(actionMap)) {
          const msg = `forkAction ${this.logLabel} has an invalid action parameter`;
          this.log.warn(msg);
          resolve(Action.createFailureOutcome('Error during ForkAction', new Error(msg), results));
          return;
        }

        Object.keys(actionMap).forEach((actionId) => {
          const action = actionMap[actionId];
          const actionPromise = this.actionChain.runActionStep(action);

          // keep track of results
          actionPromise.then((result) => {
            results[actionId] = result;
          });

          // capture the promise
          ongoingPromises.push(actionPromise);
        });

        // when all actions (and their steps) have been completed, return the result
        Promise.all(ongoingPromises)
          .then(() => {
            resolve(Action.createOutcome('join', results));
          })
          .catch((e) => {
            resolve(Action.createFailureOutcome('Error during ForkAction', e, results));
          });
      });
    }

    /**
     * Sets the context for the ForkAction instance.
     *
     * @param {Object} actionChain - The action chain to be used for executing actions.
     * @param {Object} outcomesMap - A map of outcome IDs to their corresponding actions.
     */
    setContext(actionChain, outcomesMap) {
      this.actionChain = actionChain;
      this.outcomesMap = outcomesMap;
    }
  }

  return ForkAction;
});

